/* Synchronize the independent checkout block with the original add-to-cart form. */
(() => {
  if (customElements.get('separate-product-checkout')) return;

  customElements.define('separate-product-checkout', class extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[id^="MainProduct-"]');
      if (!this.root) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.queueSync = () => {
        if (this.queued) return;
        this.queued = true;
        queueMicrotask(() => {
          this.queued = false;
          if (this.isConnected) this.sync();
        });
      };
      this.onSourceEvent = (event) => {
        if (!this.contains(event.target)) this.queueSync();
      };
      this.root.addEventListener('input', this.onSourceEvent, options);
      this.root.addEventListener('change', this.onSourceEvent, options);
      this.root.addEventListener('reset', this.queueSync, options);
      // Guard pointer, keyboard and fallback form submission immediately before use.
      for (const type of ['pointerdown', 'click', 'keydown', 'submit']) {
        this.addEventListener(type, (event) => {
          if (type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
          if (!this.sync()) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        }, { ...options, capture: true });
      }
      this.observer = new MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) this.queueSync();
      });
      this.observer.observe(this.root, {
        subtree: true, childList: true, attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'value', 'min', 'max', 'step', 'checked']
      });
      this.queueSync();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
    }

    sync() {
      const source = document.getElementById(this.dataset.sourceForm);
      const target = this.querySelector('form');
      const button = source?.querySelector('[name="add"]');
      const sourceId = source?.querySelector('[name="id"]');
      const message = this.querySelector('[data-checkout-message]');
      if (!target) return false;

      let ready = !!(source && this.root.contains(source) && button && sourceId?.value &&
        !sourceId.disabled && !button.disabled && button.getAttribute('aria-disabled') !== 'true' &&
        source.closest('product-form')?.dataset.hideErrors !== 'true');
      let changedId = false;
      let changedQuantity = false;
      if (ready) {
        const data = new FormData(source);
        const quantityControl = Array.from(source.elements).find((el) => el.name === 'quantity' && !el.disabled);
        // Do not allow accelerated checkout to bypass required product inputs or quantity rules.
        ready = Array.from(source.elements).every((el) => !el.willValidate || el.validity.valid);
        const extra = [];
        for (const [name, value] of data.entries()) {
          if (name !== 'selling_plan' && !name.startsWith('properties[')) continue;
          if (value instanceof File) {
            if (value.size || value.name) ready = false;
            continue;
          }
          extra.push([name, value]);
        }
        const id = target.querySelector('[name="id"]');
        const quantity = target.querySelector('[name="quantity"]');
        const nextId = String(data.get('id') || '');
        const nextQuantity = String(data.get('quantity') || quantityControl?.min || '1');
        changedId = id.value !== nextId;
        changedQuantity = quantity.value !== nextQuantity;
        id.value = nextId;
        quantity.value = nextQuantity;
        const signature = JSON.stringify(extra);
        if (signature !== this.extraSignature) {
          const container = this.querySelector('[data-checkout-properties]');
          container.replaceChildren(...extra.map(([name, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden'; input.name = name; input.value = value;
            return input;
          }));
          this.extraSignature = signature;
        }
      }
      target.hidden = !ready;
      target.inert = !ready;
      target.querySelector('[name="id"]').disabled = !ready;
      if (message) message.hidden = ready;
      if (changedId) target.querySelector('[name="id"]').dispatchEvent(new Event('change', { bubbles: true }));
      if (changedQuantity) target.querySelector('[name="quantity"]').dispatchEvent(new Event('change', { bubbles: true }));
      return ready;
    }
  });
})();
